import { Injectable } from '@angular/core';
import { DataService } from './data.service';

export interface LotteryApplicantRequest {
  applicationId: string;
  weight: number;
}

export interface LotteryRunRequest {
  applicants: LotteryApplicantRequest[];
 
  financialYearId?: number;
  departmentId?: number;
  dbtGoId?: number;
  dbtSchemeId?: number;
  dbtComponentId?: number;
  dbtProductId?: number;
}

export interface CommunityTarget {
  communityType: number;
  communityName: string;
  totalMachinery: number;
  alreadyBooked: number;
  remainingTarget: number;
}

export interface CommunityLotteryRun {
  communityType: number;
  communityName: string;
  targetCount: number;
  applicationCount: number;
  selectedCount: number;
}

export interface LotteryDrillDownRow {
  id: number;
  name: string;
  totalTarget: number;
  applicationsReceived: number;
  benefittedCount: number;
  balanceTarget: number;
  villageCount: number;
  applicationPercentage: number;
  benefittedPercentage: number;
}

export interface LotteryBookingListResponse {
  beneficiaries: { bookingRefNo: string; farmerName: string; communityType: number; community: string }[];
  noOfTargets: number;
  communityTargets: CommunityTarget[];
}

export interface LotteryBeneficiaryRow {
  bookingId: number;
  bookingReferenceNo: string;
  bookingDate: string;
  farmerName: string;
  mobileNumber: string;
  farmerCommunity: string;
  farmerType: string;
  productName: string;
  currentStatus: string;
  bookingStatus: string;
}

export interface LotteryDetailsParticipant {
  bookingId: number;
  applicationId: string;
  farmerName: string;
  mobileNumber: string;
  community: string;
  villageName: string;
  productName: string;
  rank: number;
  selected: boolean;
  status: string;
}

export interface LotterySubsidyPattern {
  remainingTarget: number;
  remainingSubsidyAmount: number;
  rows: CommunityLotteryRun[];
}

export interface LotteryRunSummary {
  id: number;
  lotteryRunRefNo: string;
  lotteryRunTs: string;
  totalTarget: number;
  totalApplication: number;
  benefitedCount: number;
  status: string;
  // This run's own participants only - not merged/deduped against any other run below.
  selectedParticipants: LotteryDetailsParticipant[];
  notSelectedParticipants: LotteryDetailsParticipant[];
}

export interface LotteryDetails {
  lotteryDate: string;
  componentName: string;
  productName: string;
  // Every individual lottery run behind this scope (there can be more than one - e.g. the
  // lottery was run more than once the same day for the same scope) - one expansion panel and
  // one Rerun action per run.
  lotteryRuns: LotteryRunSummary[];
}

export interface LotteryRerunResultRow {
  applicationId: string;
  weight: number;
  communityName: string;
  baseScore: string;
  lotteryScore: string;
  rank: number;
  selected: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LotteryService {

  constructor(private dataService: DataService) { }

  getBookingList(
    financialYearId: number, regionId: number, divisionId: number,
    subDivisionId: number, villageId: number,
    departmentId: number, dbtGoId: number, dbtSchemeId: number, dbtComponentId: number, dbtProductId: number,
    refresh: boolean = true
  ) {
    return this.dataService.getData(
      `/api/lottery/bookinglist/${financialYearId || 0}/${regionId || 0}` +
      `/${divisionId || 0}/${subDivisionId || 0}/${villageId || 0}` +
      `/${departmentId || 0}/${dbtGoId || 0}/${dbtSchemeId || 0}/${dbtComponentId || 0}/${dbtProductId || 0}`,
      refresh
    );
  }

  runLottery(request: LotteryRunRequest) {
    return this.dataService.post('/api/lottery', request);
  }

  rerunLottery(lotteryRunRefNo: string) {
    // POST, not GET: lotteryRunRefNo (booking ref no format, e.g. "260810/SMAM/IND/05") contains
    // literal "/" characters, which breaks a GET route segment even URL-encoded as %2F.
    return this.dataService.post('/api/lottery/rerun', { lotteryRunRefNo });
  }

  getDashboardList(
    financialYearId: number, departmentId: number, month: number,
    dbtGoId: number, dbtSchemeId: number, dbtComponentId: number, dbtProductId: number, refresh: boolean = true
  ) {
    return this.dataService.getData(
      `/api/lottery/lotterydashboard/${financialYearId || 0}/${departmentId || 0}/${month || 0}` +
      `/${dbtGoId || 0}/${dbtSchemeId || 0}/${dbtComponentId || 0}/${dbtProductId || 0}`,
      refresh
    );
  }

  getDrillDownList(
    financialYearId: number, departmentId: number, month: number, dbtGoId: number, dbtSchemeId: number,
    lotteryRunId: number, divisionId: number, subDivisionId: number, refresh: boolean = true
  ) {
    return this.dataService.getData(
      `/api/lottery/lotterydashboarddrilldown/${financialYearId || 0}/${departmentId || 0}/${month || 0}` +
      `/${dbtGoId || 0}/${dbtSchemeId || 0}/${lotteryRunId || 0}/${divisionId || 0}/${subDivisionId || 0}`,
      refresh
    );
  }

  getBeneficiaryList(
    financialYearId: number, departmentId: number, month: number, dbtGoId: number, dbtSchemeId: number,
    lotteryRunId: number, divisionId: number, subDivisionId: number, villageId: number, refresh: boolean = true
  ) {
    return this.dataService.getData(
      `/api/lottery/beneficiarylist/${financialYearId || 0}/${departmentId || 0}/${month || 0}` +
      `/${dbtGoId || 0}/${dbtSchemeId || 0}/${lotteryRunId || 0}/${divisionId || 0}/${subDivisionId || 0}/${villageId || 0}`,
      refresh
    );
  }

  getLotteryDetails(
    financialYearId: number, departmentId: number, dbtGoId: number, dbtSchemeId: number,
    dbtComponentId: number, dbtProductId: number, lotteryRunId: number,
    divisionId: number, subDivisionId: number, villageId: number,
    lotteryDate: string, refresh: boolean = true
  ) {
    return this.dataService.getData(
      `/api/lottery/lotterydetails/${financialYearId || 0}/${departmentId || 0}/${dbtGoId || 0}/${dbtSchemeId || 0}` +
      `/${dbtComponentId || 0}/${dbtProductId || 0}/${lotteryRunId || 0}` +
      `/${divisionId || 0}/${subDivisionId || 0}/${villageId || 0}/${encodeURIComponent(lotteryDate || '0')}`,
      refresh
    );
  }

  getSubsidyPattern(
    financialYearId: number, departmentId: number, month: number, dbtGoId: number, dbtSchemeId: number,
    lotteryRunId: number, divisionId: number, subDivisionId: number, villageId: number, refresh: boolean = true
  ) {
    return this.dataService.getData(
      `/api/lottery/subsidypattern/${financialYearId || 0}/${departmentId || 0}/${month || 0}` +
      `/${dbtGoId || 0}/${dbtSchemeId || 0}/${lotteryRunId || 0}/${divisionId || 0}/${subDivisionId || 0}/${villageId || 0}`,
      refresh
    );
  }
}
